import type { BulkMaterial, CargoMaterial, Goods } from '#domain/model/Goods.ts';
import type { StopType } from '#domain/model/Stop.ts';
import type { TransportOrder } from '#domain/model/TransportOrder.ts';

export const hasDeviation = (target?: number | string, actual?: number | string) => {
    if (target === undefined) {
        return false;
    }
    return actual != null && target !== actual;
};

const hasCargoMaterialDeviation = (stopType: StopType, material: CargoMaterial): boolean => {
    switch (stopType) {
        case 'load':
            return material.units.some(
                unit =>
                    hasDeviation(unit.quantity.target, unit.quantity.loaded) ||
                    hasDeviation(unit.weight?.target, unit.weight?.loaded) ||
                    hasDeviation(unit.loadingMeter?.target, unit.loadingMeter?.loaded)
            );
        case 'unload':
            return material.units.some(
                unit =>
                    hasDeviation(unit.quantity.target, unit.quantity.unloaded) ||
                    hasDeviation(unit.weight?.target, unit.weight?.unloaded) ||
                    hasDeviation(unit.loadingMeter?.target, unit.loadingMeter?.unloaded)
            );
        case 'unknown':
        case 'service':
            return false;
    }
};

const hasBulkMaterialDeviation = (stopType: StopType, material: BulkMaterial): boolean => {
    switch (stopType) {
        case 'load':
            return material.units.some(unit => hasDeviation(unit.quantity.target.value, unit.quantity.loaded?.value));
        case 'unload':
            return material.units.some(unit => hasDeviation(unit.quantity.target.value, unit.quantity.unloaded?.value));
        case 'unknown':
        case 'service':
            return false;
    }
};

export const hasStopDeviation = (stopType: StopType, transportOrders: TransportOrder[]): boolean =>
    transportOrders.some(order => hasTransportOrderDeviation(stopType, order));

export const hasTransportOrderDeviation = (stopType: StopType, transportOrder: TransportOrder): boolean => {
    if (stopType === 'service') {
        return false;
    }
    return transportOrder.goods ? hasGoodsDeviation(stopType, transportOrder.goods) : false;
};

export const hasGoodsDeviation = (stopType: StopType, goods: Goods) => {
    switch (goods.type) {
        case 'cargo-material':
            return hasCargoMaterialDeviation(stopType, goods);
        case 'bulk-material':
            return hasBulkMaterialDeviation(stopType, goods);
        case 'swap-body':
        case 'customer-trailer':
            return false;
    }
};
